/**
 * **첫 결제 보너스(무료 1달)의 기록 규약** — 워크스페이스당 한 번, 체험·만료 뒤 결제를 합쳐 센다.
 *
 * ```
 *   받았는가   그 워크스페이스(workspace_id)의 결제 내역 중 하나라도 `bonus` 가 있다
 *   적는 곳    서버 workspace.plan.update 만 — PaymentHistory/{주문번호}.bonus = { type, benefit }
 *   클라      `bonus` 를 쓰지 못한다(payment.history.set|update 가 걷어 낸다)
 * ```
 * 순수 함수다 — `'use client'` 를 붙이지 않는다. 기간 계산은 `plan-period` 의 `firstPaidPeriod`.
 */

import { normalizePlanId } from './plan-utils';
import { readPlanEndDate } from './plan-active';

import type { PaymentBonusType, CurrentSubscriptionPeriod } from './plan-period';

/** 결제 내역 문서에서 보너스를 담는 필드 이름 */
export const PAYMENT_BONUS_FIELD = 'bonus';

/** 결제 내역에 적는 혜택 문구 */
export const PAYMENT_BONUS_BENEFIT = '1달 무료';

export type PaymentBonusRecord = { type: PaymentBonusType; benefit: string };

/** 결제 내역에 병합할 `bonus` 값 */
export function paymentBonusRecord(type: PaymentBonusType): PaymentBonusRecord {
  return { type, benefit: PAYMENT_BONUS_BENEFIT };
}

/**
 * 결제 내역 행들 중 보너스를 받은 기록이 있는가 — `bonus` 가 객체로 적힌 행이 하나라도 있으면 받았다.
 * @param rows 그 워크스페이스의 결제 내역(`payment.history.get` · 서버 질의)
 */
export function hasReceivedPaymentBonus(rows: ReadonlyArray<unknown> | null | undefined): boolean {
  return (rows ?? []).some((row) => {
    const bonus = (row as Record<string, unknown> | null | undefined)?.[PAYMENT_BONUS_FIELD];
    return bonus !== null && typeof bonus === 'object';
  });
}

/**
 * 브라우저가 들고 있는 워크스페이스(`user.workspaces` 의 한 줄) → `firstPaidPeriod` 의 지금 구독.
 * 만료 표시는 `subscriptionPlan.planStatus` 와 워크스페이스 `type` 둘 다 본다(서버 `readRenewalSubscription` 과 같은 판정).
 * @returns 구독이 없으면 `null`
 */
export function subscriptionPeriodOfWorkspace(workspace: unknown): CurrentSubscriptionPeriod | null {
  const ws = workspace as { type?: unknown; subscriptionPlan?: Record<string, unknown> | null } | null | undefined;
  const plan = ws?.subscriptionPlan;
  if (!plan || typeof plan !== 'object') return null;
  const markedExpired = plan.planStatus === 'expired' || ws?.type === 'expired';
  return {
    planId: normalizePlanId(plan.planId as string | undefined) ?? normalizePlanId(plan.planName as string | undefined),
    planStatus: markedExpired ? 'expired' : (plan.planStatus as string | null | undefined),
    planEndMs: readPlanEndDate(plan)?.getTime() ?? null,
  };
}
